// swavai-frontend/src/pages/Practice/Practice.js
import React, { useState, useEffect, useRef } from "react";
import "./Practice.css";
import axios from "axios";
import api from '../../utils/axios';
import CenteredLoading from '../../components/CenteredLoading';
import { MdDevices, MdMic, MdInsights } from 'react-icons/md';
import { BsPlusLg } from 'react-icons/bs';
import { BsMic } from 'react-icons/bs';
import { FaPowerOff } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, CartesianGrid, Scatter
} from 'recharts';
import * as THREE from 'three';
import { createNoise3D } from 'simplex-noise';

const vertexShader = `
  varying float vElevation;
  void main() {
    vElevation = position.z;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  varying float vElevation;
  uniform vec3 colorLow;
  uniform vec3 colorHigh;
  
  void main() {
    float alpha = 0.8;
    vec3 color = mix(colorLow, colorHigh, clamp(vElevation * 5.0, 0.0, 1.0));
    gl_FragColor = vec4(color, alpha);
  }
`;

const formatXTick = (timestamp, range) => {
  const date = new Date(timestamp);
  const options = { timeZone: 'America/Toronto' };
  if (range === '24h') {
    return date.toLocaleTimeString('en-US', { ...options, hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { ...options, month: 'short', day: 'numeric' });
};

const generateTicks = (data, range) => {
  if (!data || data.length === 0) return [];

  const start = data[0].date;
  const end = data[data.length - 1].date;
  const msInHour = 3600000;
  const msInDay = 86400000;

  const interval = range === '24h' ? msInHour * 2 : msInDay; // Every 2 hours or daily
  const numTicks = range === '24h' ? 12 : 7;

  return Array.from({ length: numTicks }, (_, i) =>
    start + (i * ((end - start) / (numTicks - 1)))
  );
};

const CustomTooltip = ({ active, payload, label, timeRange }) => {
  if (!active || !payload || !payload.length) return null;

  const date = new Date(label);
  const formattedDate = timeRange === '24h'
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString();

  return (
    <div className="custom-tooltip">
      <p className="date">{formattedDate}</p>
      {payload.map((entry, index) => (
        <div key={index} className="metric">
          <span style={{ color: entry.color }}>●</span>
          <span className="label">{entry.name}: </span>
          <span className="value">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const Practice = () => {
  useEffect(() => {
    document.title = "SwavAI - Practice";
  }, []);

  const navigate = useNavigate(); // Move useNavigate to top
  const [audioChunks, setAudioChunks] = useState([]); // Move useState to top
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const mediaRecorderRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [minutesRemaining, setMinutesRemaining] = useState(null);

  const [experience, setExperience] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [timeRange, setTimeRange] = useState('24h'); // '24h' or '7d'
  const [averages, setAverages] = useState({ tone: 0, confidence: 0, clarity: 0, efficiency: 0, pace: 0 });
  const [feedbackType, setFeedbackType] = useState('general'); // Add state for feedback type
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const noiseRef = useRef(createNoise3D());
  const frameRef = useRef(null);

  const fetchRemainingMinutes = async () => {
    try {
      const response = await api.get('/recording/minutes-remaining');
      const minutes = response.data.minutesRemaining;
      setMinutesRemaining(minutes !== null && minutes !== undefined ? minutes : 0);
      console.log('minutesRemaining:', minutes);
    } catch (error) {
      console.error('Error fetching remaining minutes:', error);
      setMinutesRemaining(0); // Default to 0 in case of error
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        await fetchRemainingMinutes();
        setLoading(false);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    const fetchRemainingMinutes = async () => {
      try {
        const response = await api.get('/recording/minutes-remaining');
        setMinutesRemaining(response.data.minutesRemaining ?? 0);
      } catch (error) {
        console.error('Error fetching minutes:', error);
        setMinutesRemaining(0);
      }
    };

    fetchRemainingMinutes();

    // Refresh every minute if recording
    if (isRecording) {
      const interval = setInterval(fetchRemainingMinutes, 60000);
      return () => clearInterval(interval);
    }
  }, [isRecording]);

  const [visibleLines, setVisibleLines] = useState({
    tone: true,
    confidence: true,
    clarity: true,
    efficiency: true,
    pace: true
  });

  const handleLegendClick = (dataKey) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [chartDataResponse, experienceResponse, sessionsResponse, insightResponse] = await Promise.all([
          api.get(`/analytics/chart-data/${timeRange}`),
          api.get("/user/experience"),
          api.get("/session/all"),
        ]);

        // Log raw responses
        console.log('Chart data response:', chartDataResponse.data);
        console.log('Experience response:', experienceResponse.data);
        console.log('Sessions response:', sessionsResponse.data);

        // Validate and transform chart data
        const rawData = chartDataResponse.data.data || [];
        const formattedChartData = rawData.map(item => ({
          ...item,
          date: new Date(item.date).getTime(),
          tone: Number(item.tone) || 0,
          confidence: Number(item.confidence) || 0,
          clarity: Number(item.clarity) || 0,
          efficiency: Number(item.efficiency) || 0,
          pace: Number(item.pace) || 0
        }));

        // Transform averages with validation
        const rawAverages = chartDataResponse.data.averages || {};
        const processedAverages = {
          tone: parseFloat(rawAverages.tone) || 0,
          confidence: parseFloat(rawAverages.confidence) || 0,
          clarity: parseFloat(rawAverages.clarity) || 0,
          efficiency: parseFloat(rawAverages.efficiency) || 0,
          pace: parseFloat(rawAverages.pace) || 0
        };

        // Log processed data
        console.log('Formatted chart data:', formattedChartData);
        console.log('Processed averages:', processedAverages);

        if (formattedChartData.length === 0) {
          console.warn('No chart data available');
        }

        // Update state with validated data
        setChartData(formattedChartData);
        setAverages(processedAverages);
        setExperience(experienceResponse.data || {});
        setSessions(sessionsResponse.data || []);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load insights data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  const getPerformanceLevel = (avgScore) => {
    if (avgScore === null || avgScore === undefined) return 'No Data';
    if (avgScore < 0) return 'Critical';
    if (avgScore <= 30) return 'Needs Improvement';
    if (avgScore <= 50) return 'Satisfactory';
    if (avgScore <= 80) return 'Good';
    return 'Exceptional';
  };

  console.log('Averages:', averages);

  useEffect(() => {
    if (isRecording) {
      // Periodically update remaining minutes while recording
      const interval = setInterval(fetchRemainingMinutes, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isRecording]);

  const handleAddDevice = () => {
    const newDevice = {
      id: devices.length + 1,
      name: `Device ${devices.length + 1}`,
      status: "Off",
    };
    setDevices([...devices, newDevice]);
  };


  const toggleDeviceStatus = (id) => {
    setDevices(
      devices.map((device) =>
        device.id === id
          ? { ...device, status: device.status === "On" ? "Off" : "On" }
          : device
      )
    );
  };

  const startRecording = async () => {
    try {
      console.log("Starting recording...");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      const chunks = [];
      recorder.ondataavailable = event => {
        chunks.push(event.data);
      };

      recorder.onstart = () => {
        setAudioChunks(chunks);
        recordingStartTimeRef.current = Date.now();
        setIsRecording(true);
        setIsListening(true); // Start animation
        visualize();
      };
      recorder.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Failed to start recording.");
    }
  };

  const stopRecording = () => {
    if (!isRecording || isProcessing) return;
    setIsProcessing(true);

    mediaRecorderRef.current.stop();

    mediaRecorderRef.current.onstop = async () => {
      console.log("Recording stopped");
      const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
      const audioFile = new File([audioBlob], 'recording.wav', { type: 'audio/wav' });

      const recordingEndTime = Date.now();
      const recordingDuration = (recordingEndTime - recordingStartTimeRef.current) / 1000;

      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('feedbackType', feedbackType); // Include feedback type

      const token = localStorage.getItem('token');
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const sessionId = Date.now();
      formData.append('userId', decodedToken.userId);
      formData.append('sessionId', sessionId);
      formData.append('duration', parseFloat(recordingDuration));

      try {
        const response = await api.post('/recording/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('Audio uploaded:', response.data);

        // Navigate to the sessions page with the new session ID
        navigate(`/sessions?session=${sessionId}`);

      } catch (error) {
        console.error('Error uploading recording:', error);
        alert('Failed to upload recording.');
      } finally {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        setIsProcessing(false);
        setIsListening(false);
      }
    };
  };

  const handleRecordingToggle = async () => {
    if (isProcessing) return;

    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const RecordingControls = ({ minutesRemaining, isRecording, isProcessing, handleRecordingToggle }) => {
    if (minutesRemaining === 0) {
      return (
        <div className="recording-controls">
          <div className="minutes-depleted">
            <p>You've used all your recording minutes for today</p>
            <a href="/upgrade" className="upgrade-button">
              <BsPlusLg /> Get More Minutes
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="recording-controls">
        {!isProcessing && (
          <button
            className={`record-button ${isRecording ? 'recording' : ''}`}
            onClick={handleRecordingToggle}
            disabled={isProcessing || minutesRemaining === 0}
          >
            <MdMic />
            <span>{isRecording ? "Stop Recording" : "Start Recording"}</span>
          </button>
        )}
      </div>
    );
  };

  const visualize = () => {
    if (!canvasRef.current) return;

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    // Increased dimensions
    const width = 800;
    const height = 200;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Adjusted camera settings for a wider view
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 1.2; // Moved camera closer
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      precision: 'highp'  // Higher precision rendering
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio); // Match device pixel ratio
    rendererRef.current = renderer;

    // Increased geometry width and resolution
    const geometry = new THREE.PlaneGeometry(6, 1, 150, 25); // Wider plane, more segments
    const material = new THREE.ShaderMaterial({
      uniforms: {
        colorLow: { value: new THREE.Color(0xffd700) },  // Gold color for low amplitude
        colorHigh: { value: new THREE.Color(0xff3030) }  // Red color for high amplitude
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      wireframe: true,
      transparent: true
    });

    const mesh = new THREE.Mesh(geometry, material);
    // Set static rotation and position
    mesh.rotation.x = -0.5;  // Fixed angle
    mesh.position.y = -0.1;  // Slight vertical adjustment
    scene.add(mesh);
    meshRef.current = mesh;

    let time = 0;
    let smoothedIntensity = 0; // Add smoothing
    const smoothingFactor = 0.15; // Lower = smoother transitions

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      time += 0.008; // Slower base animation speed

      // Enhanced audio reactivity with smoothing
      let targetIntensity = 0;
      if (analyserRef.current) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // More sophisticated audio analysis with focus on mid-range frequencies
        const midFreqIntensity = dataArray.slice(10, 100).reduce((a, b) => a + b) / 22950;
        targetIntensity = Math.min(midFreqIntensity * 1.2, 1.0); // Cap the maximum intensity
      }

      // Smooth the intensity changes
      smoothedIntensity += (targetIntensity - smoothedIntensity) * smoothingFactor;

      // Add a minimum movement during quiet periods
      const baseAmp = 0.05; // Reduced base amplitude
      const baseFreq = 0.8; // Slower base frequency

      // Update vertices with smoother effect
      const positions = geometry.attributes.position;
      const amp = baseAmp + smoothedIntensity * 0.25; // Reduced audio influence
      const freq = baseFreq + smoothedIntensity * 1.5; // Reduced frequency reactivity

      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);

        // Gentler multi-layered noise
        const noise1 = noiseRef.current(x * freq, y * freq, time) * amp;
        const noise2 = noiseRef.current(x * freq * 1.5, y * freq * 1.5, time * 0.8) * (amp * 0.2);

        const z = noise1 + noise2;
        positions.setZ(i, z);
      }

      positions.needsUpdate = true;

      renderer.render(scene, camera);
    };

    animate();
  };

  // Clean up Three.js on component unmount
  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (meshRef.current) {
        meshRef.current.geometry.dispose();
        meshRef.current.material.dispose();
      }
    };
  }, []);

  if (loading) return <CenteredLoading />;
  if (error) return <div className="error-message">{error}</div>;
  if (!experience || !chartData.length) {
    return (
      <>
        <div className="dashboard">
          <section className="recording-session">
            {minutesRemaining !== null && (
              <div className="minutes-status">
                <span>Practice Minutes Remaining: <span className="time">{minutesRemaining.toFixed(0)}m</span></span>
                <span className="divider"></span>
                <a href="/upgrade" className="get-more-link">Get More →</a>
              </div>
            )}
            <div className="recording-content">
              <div className="feedback-type-selector">
                <label htmlFor="feedback-type">Select Feedback Type:</label>
                <select
                  id="feedback-type"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="job interview">Job Interview</option>
                  <option value="academic presentation">Academic Presentation</option>
                  <option value="business presentation">Business Presentation</option>
                  <option value="sales pitch">Sales Pitch</option>
                  <option value="startup pitch">Startup Pitch</option>
                </select>
              </div>
              <RecordingControls
                minutesRemaining={minutesRemaining}
                isRecording={isRecording}
                isProcessing={isProcessing}
                handleRecordingToggle={handleRecordingToggle}
              />
              <div className={`sound-wave ${isProcessing ? 'processing' : ''} ${isListening ? 'listening' : ''}`}>
                {isProcessing ? (
                  <div className="loading-animation">
                    <div className="loading-spinner"></div>
                    <div className="loading-dots"></div>
                  </div>
                ) : (
                  !isRecording && !isProcessing && minutesRemaining > 0 &&
                  <div className="start-prompt">
                    <MdMic />
                    <p>Click "Start Recording" to begin</p>
                  </div>
                )}
                {!isProcessing && <canvas ref={canvasRef} width="600" height="100" />}
                {isProcessing && (
                  <div className="processing-overlay">
                    <div className="loading-spinner"></div>
                    <p>Analyzing audio...</p>
                    <p><i>Please do not refresh the page</i></p>
                  </div>
                )}
              </div>
            </div>
          </section>
          {/* <section className="device-management">
          <div className="section-header">
            <MdDevices className="section-icon" />
            <h2>Device Management</h2>
          </div>
          <div className="device-list">
            {devices.length === 0 ? (
              <div className="empty-state">
                <MdDevices className="empty-state-icon" />
                <p className="empty-state-text">No devices connected yet</p>
                <button className="empty-state-action" >
                  <BsPlusLg /> Add Your First Device
                </button>
              </div>
            ) : (
              <div className="devices-grid">
                {devices.map((device) => (
                  <div key={device.id} className="device-card">
                    <h3>{device.name}</h3>
                    <p className={`status ${device.status.toLowerCase()}`}>
                      <span className="status-dot"></span>
                      Status: {device.status}
                    </p>
                    <button
                      onClick={() => toggleDeviceStatus(device.id)}
                      className={`toggle-btn ${device.status.toLowerCase()}`}
                    >
                      <FaPowerOff />
                      <span>{device.status === "On" ? "Turn Off" : "Turn On"}</span>
                    </button>
                  </div>
                ))}
                <button className="add-device-card" onClick={handleAddDevice}>
                  <BsPlusLg />
                  <span>Add Device</span>
                </button>
              </div>
            )}
          </div>
        </section> */}
        </div>
        <div className="empty-state">
          <MdInsights className="empty-state-icon" />
          <p className="empty-state-text">
            Complete a recent recording session to see insights and analytics
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="dashboard">
        <section className="recording-session">
          {minutesRemaining !== null && (
            <div className="minutes-status">
              <span>Practice Minutes Remaining: <span className="time">{minutesRemaining.toFixed(0)}m</span></span>
              <span className="divider"></span>
              <a href="/upgrade" className="get-more-link">Get More →</a>
            </div>
          )}
          <div className="recording-content">
            <div className="feedback-type-selector">
              <label htmlFor="feedback-type">Select Feedback Type:</label>
              <select
                id="feedback-type"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
              >
                <option value="general">General</option>
                <option value="job interview">Job Interview</option>
                <option value="academic presentation">Academic Presentation</option>
                <option value="business presentation">Business Presentation</option>
                <option value="sales pitch">Sales Pitch</option>
                <option value="startup pitch">Startup Pitch</option>
              </select>
            </div>
            <RecordingControls
              minutesRemaining={minutesRemaining}
              isRecording={isRecording}
              isProcessing={isProcessing}
              handleRecordingToggle={handleRecordingToggle}
            />
            <div className={`sound-wave ${isProcessing ? 'processing' : ''} ${isListening ? 'listening' : ''}`}>
              {isProcessing ? (
                <div className="loading-animation">
                  <div className="loading-spinner"></div>
                  <div className="loading-dots"></div>
                </div>
              ) : (
                !isRecording && !isProcessing && minutesRemaining > 0 &&
                <div className="start-prompt">
                  <MdMic />
                  <p>Click "Start Recording" to begin</p>
                </div>
              )}
              {!isProcessing && <canvas ref={canvasRef} width="600" height="100" />}
              {isProcessing && (
                <div className="processing-overlay">
                  <div className="loading-spinner"></div>
                  <p>Analyzing audio...</p>
                  <p><i>Please do not refresh the page</i></p>
                </div>
              )}
            </div>
          </div>
        </section>
        {/* <section className="device-management">
          <div className="section-header">
            <MdDevices className="section-icon" />
            <h2>Device Management</h2>
          </div>
          <div className="device-list">
            {devices.length === 0 ? (
              <div className="empty-state">
                <MdDevices className="empty-state-icon" />
                <p className="empty-state-text">No devices connected yet</p>
                <button className="empty-state-action" >
                  <BsPlusLg /> Add Your First Device
                </button>
              </div>
            ) : (
              <div className="devices-grid">
                {devices.map((device) => (
                  <div key={device.id} className="device-card">
                    <h3>{device.name}</h3>
                    <p className={`status ${device.status.toLowerCase()}`}>
                      <span className="status-dot"></span>
                      Status: {device.status}
                    </p>
                    <button
                      onClick={() => toggleDeviceStatus(device.id)}
                      className={`toggle-btn ${device.status.toLowerCase()}`}
                    >
                      <FaPowerOff />
                      <span>{device.status === "On" ? "Turn Off" : "Turn On"}</span>
                    </button>
                  </div>
                ))}
                <button className="add-device-card" onClick={handleAddDevice}>
                  <BsPlusLg />
                  <span>Add Device</span>
                </button>
              </div>
            )}
          </div>
        </section> */}
      </div>
      <div className="insights">
        <section className="skill-experience">
          <div className="chart-header">
            <h2>{timeRange === '24h' ? '24 Hour Performance' : '7 Day Performance'}</h2>
            <div className="range-selector">
              <button
                className={`range-btn ${timeRange === '24h' ? 'active' : ''}`}
                onClick={() => setTimeRange('24h')}
              >
                Last 24 Hours
              </button>
              <button
                className={`range-btn ${timeRange === '7d' ? 'active' : ''}`}
                onClick={() => setTimeRange('7d')}
              >
                Last 7 Days
              </button>
            </div>
          </div>
          <div className="skill-experience-grid">
            {["tone", "confidence", "clarity", "efficiency", "pace"].map(skill => {
              const avgScore = averages[skill];
              const hasData = avgScore !== undefined && avgScore !== null;
              const performanceLevel = getPerformanceLevel(avgScore);

              const skillDefinitions = {
                tone: "Tone: The emotion or attitude conveyed in your speech.",
                confidence: "Confidence: The conviction and assertiveness in your speaking.",
                clarity: "Clarity: The clearness and coherence of your speech.",
                efficiency: "Efficiency: The effectiveness and productivity of your speech.",
                pace: "Pace: The speed and rhythm of your speech."
              };

              return (
                <div key={skill} className="skill-card">
                  <h3>{skill.charAt(0).toUpperCase() + skill.slice(1)}</h3>
                  <p className="score-value" style={{
                    color: hasData ? (
                      avgScore < 0 ? '#f44336' : // Critical - red
                        avgScore <= 30 ? '#FF8C00' : // Needs Improvement - dark orange
                          avgScore <= 50 ? '#FFC107' : // Satisfactory - amber
                            avgScore <= 80 ? '#8BC34A' : // Good - light green
                              '#4CAF50'                    // Exceptional - medium green
                    ) : 'var(--text-secondary)'
                  }}>
                    {hasData ? avgScore.toFixed(1) : 'N/A'}
                  </p>
                  <div style={{ padding: '30px' }}></div>
                  <span className="performance-level">
                    {performanceLevel}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="experience-chart">
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis
                dataKey="date"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(tick) => formatXTick(tick, timeRange)}
                interval="preserveEnd"
                ticks={generateTicks(chartData, timeRange)}
              />
              <YAxis
                domain={[-100, 100]}
                ticks={[-100, -50, 0, 50, 100]}
                label={{
                  value: 'Score',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: 'var(--text-secondary)' }
                }}
              />
              <Tooltip content={<CustomTooltip timeRange={timeRange} />} />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                onClick={(e) => handleLegendClick(e.dataKey)}
                wrapperStyle={{
                  cursor: 'pointer'
                }}
              />
              <ReferenceLine y={0} stroke="white" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="tone"
                stroke="#8884d8"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 8 }}
                hide={!visibleLines.tone}
                strokeOpacity={visibleLines.tone ? 1 : 0.2}
              />
              <Line
                type="monotone"
                dataKey="confidence"
                stroke="#82ca9d"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 8 }}
                hide={!visibleLines.confidence}
                strokeOpacity={visibleLines.confidence ? 1 : 0.2}
              />
              <Line
                type="monotone"
                dataKey="clarity"
                stroke="#ffc658"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 8 }}
                hide={!visibleLines.clarity}
                strokeOpacity={visibleLines.clarity ? 1 : 0.2}
              />
              <Line
                type="monotone"
                dataKey="efficiency"
                stroke="#ff7300"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 8 }}
                hide={!visibleLines.efficiency}
                strokeOpacity={visibleLines.efficiency ? 1 : 0.2}
              />
              <Line
                type="monotone"
                dataKey="pace"
                stroke="#8dd1e1"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 8 }}
                hide={!visibleLines.pace}
                strokeOpacity={visibleLines.pace ? 1 : 0.2}
              />

              {/* Favorite Session Markers */}
              <Scatter
                data={chartData.filter(d => d.isFavorite)}
                fill="var(--gold)"
                shape="star"
                dataKey="date"
                yAxisId={0}
              />
            </LineChart>
          </ResponsiveContainer>
        </section>
      </div>
    </>
  );
};

export default Practice;